import { useOverview, formatCents, centsToShort } from "@/lib/money";
import { PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Area, AreaChart } from "recharts";
import { ArrowUpRight, ArrowDownRight, Wallet, TrendingUp, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import NetWorthProjection from "./NetWorthProjection";

export default function MoneyOverview() {
  const { data, loading } = useOverview();

  if (loading || !data) return <div className="text-sm text-surface-onVariant">Loading finances…</div>;

  const netPositive = data.net_position_cents >= 0;
  const monthPositive = data.month.net_cents >= 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="money-overview">
      {/* Hero: net position */}
      <div className="col-span-full md:col-span-8 relative overflow-hidden rounded-hero md:rounded-heroLg bg-surface-container p-8 md:p-10">
        <div className="absolute -top-16 -left-16 w-64 h-64 rounded-full bg-primary-container/70 blur-[80px] pointer-events-none" />
        <div className="absolute -bottom-20 -right-10 w-72 h-72 rounded-full bg-secondary-container/70 blur-[90px] pointer-events-none" />
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 text-primary text-sm font-medium">
            <Wallet className="w-4 h-4" /> Net position
          </div>
          <div className="mt-3 text-4xl md:text-5xl font-medium text-surface-on tracking-tight" data-testid="net-position">
            {formatCents(data.net_position_cents)}
          </div>
          <div className="mt-2 text-sm text-surface-onVariant">As of {data.as_of}</div>
          {/* Month tiles */}
          <div className="mt-8 grid grid-cols-3 gap-4">
            <MonthTile label="This month in"  cents={data.month.income_cents}  positive icon={ArrowUpRight} />
            <MonthTile label="This month out" cents={data.month.expense_cents} negative icon={ArrowDownRight} />
            <MonthTile label="Net saved"      cents={data.month.net_cents}
                        positive={monthPositive} icon={TrendingUp}
                        sub={`Savings rate ${data.month.savings_rate}%`} />
          </div>
        </div>
      </div>

      {/* Budget health */}
      <div className="col-span-full md:col-span-4 rounded-hero bg-surface-container p-6">
        <h3 className="text-lg font-medium text-surface-on">Budget health</h3>
        <div className="mt-6 text-center">
          <div className="text-5xl font-medium text-surface-on" data-testid="budget-on-track">
            {data.budgets.on_track}
            <span className="text-surface-onVariant text-2xl">/{data.budgets.total}</span>
          </div>
          <div className="text-sm text-surface-onVariant mt-1">on track</div>
        </div>
        {data.budgets.over > 0 && (
          <div className="mt-4 bg-danger-container text-danger rounded-2xl px-4 py-2 text-sm font-medium text-center" data-testid="budget-over-flag">
            {data.budgets.over} over budget
          </div>
        )}
        <div className="mt-4 w-full bg-surface-containerHigh rounded-full h-2 overflow-hidden">
          <div className="bg-primary h-full transition-all duration-500 ease-md" style={{ width: `${data.budgets.total ? (data.budgets.on_track / data.budgets.total) * 100 : 100}%` }} />
        </div>
      </div>

      {/* Upcoming bills */}
      <div className="col-span-full md:col-span-4 rounded-hero bg-surface-container p-6">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-4 h-4 text-tertiary" />
          <h3 className="text-lg font-medium text-surface-on">Upcoming bills</h3>
        </div>
        <div className="text-sm text-surface-onVariant mb-3">
          Next 14 days · <span className="font-medium text-surface-on">{formatCents(data.total_due_soon_cents)}</span> due
        </div>
        {data.upcoming_bills.length === 0 && <p className="text-sm text-surface-onVariant">Nothing coming up. Nice.</p>}
        <ul className="space-y-2" data-testid="upcoming-bills-list">
          {data.upcoming_bills.slice(0, 6).map((b) => (
            <li key={`${b.source}-${b.id}`} className="flex items-center justify-between gap-3 py-2 border-b border-outline-variant last:border-none">
              <div className="min-w-0">
                <div className="text-sm font-medium text-surface-on truncate">{b.title}</div>
                <div className="text-xs text-surface-onVariant">{b.date}</div>
              </div>
              {b.amount_cents !== null && <span className="text-sm font-medium text-surface-on">{formatCents(b.amount_cents)}</span>}
            </li>
          ))}
        </ul>
      </div>

      {/* Spending by category donut */}
      <div className="col-span-full md:col-span-4 rounded-hero bg-surface-container p-6">
        <h3 className="text-lg font-medium text-surface-on">Where it went</h3>
        <p className="text-xs text-surface-onVariant mt-0.5">This month by category</p>
        {data.spend_by_category.length === 0 ? (
          <p className="text-sm text-surface-onVariant mt-6">No spending this month.</p>
        ) : (
          <div className="mt-4">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={data.spend_by_category} dataKey="amount_cents" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                  {data.spend_by_category.map((s) => <Cell key={s.category_id} fill={s.color} />)}
                </Pie>
                <Tooltip formatter={(v) => formatCents(v)} contentStyle={{ backgroundColor: "#FFFBFE", border: "1px solid #CAC4D0", borderRadius: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-3 space-y-1.5 text-xs" data-testid="spend-by-category">
              {data.spend_by_category.slice(0, 4).map((s) => (
                <div key={s.category_id} className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 text-surface-on">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                    {s.name}
                  </span>
                  <span className="text-surface-onVariant font-medium">{formatCents(s.amount_cents)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Cashflow 6-month */}
      <div className="col-span-full md:col-span-8 rounded-hero bg-surface-container p-6">
        <h3 className="text-lg font-medium text-surface-on">Cashflow</h3>
        <p className="text-xs text-surface-onVariant mt-0.5">Last 6 months</p>
        <div className="mt-4">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.cashflow_6mo}>
              <defs>
                <linearGradient id="inc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6750A4" stopOpacity={0.35}/>
                  <stop offset="95%" stopColor="#6750A4" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="exp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#7D5260" stopOpacity={0.35}/>
                  <stop offset="95%" stopColor="#7D5260" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#CAC4D0" vertical={false} />
              <XAxis dataKey="month" stroke="#49454F" style={{ fontSize: 12 }} />
              <YAxis stroke="#49454F" style={{ fontSize: 12 }} tickFormatter={(v) => centsToShort(v)} />
              <Tooltip formatter={(v) => formatCents(v)} contentStyle={{ backgroundColor: "#FFFBFE", border: "1px solid #CAC4D0", borderRadius: 12 }} />
              <Area type="monotone" dataKey="income_cents" stroke="#6750A4" fill="url(#inc)" strokeWidth={2} name="Income" />
              <Area type="monotone" dataKey="expense_cents" stroke="#7D5260" fill="url(#exp)" strokeWidth={2} name="Expense" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Accounts strip */}
      <div className="col-span-full rounded-hero bg-surface-container p-6">
        <h3 className="text-lg font-medium text-surface-on mb-4">Accounts</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="accounts-strip">
          {data.accounts.map((a) => (
            <div key={a.id} className="rounded-[16px] bg-surface p-4 border border-outline-variant">
              <div className="text-xs uppercase tracking-widest text-surface-onVariant">{a.type.replace("_", " ")}</div>
              <div className="text-sm font-medium text-surface-on mt-1">{a.name}</div>
              <div className={cn("text-xl font-medium mt-2", a.current_balance_cents < 0 ? "text-danger" : "text-surface-on")}>
                {formatCents(a.current_balance_cents, a.currency)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Net worth projection */}
      <NetWorthProjection />
    </div>
  );
}

function MonthTile({ label, cents, positive, negative, icon: Icon, sub }) {
  const tone = positive ? "text-primary" : negative ? "text-tertiary" : "text-surface-on";
  return (
    <div className="rounded-[16px] bg-surface p-4 border border-outline-variant">
      <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-surface-onVariant">
        <Icon className={cn("w-3 h-3", tone)} /> {label}
      </div>
      <div className={cn("text-xl md:text-2xl font-medium mt-2 tabular-nums", tone)}>{formatCents(cents)}</div>
      {sub && <div className="text-xs text-surface-onVariant mt-1">{sub}</div>}
    </div>
  );
}
